import * as Sentry from '@sentry/react'

type FeedbackForm = Awaited<ReturnType<NonNullable<ReturnType<typeof Sentry.getFeedback>>['createForm']>>

/** Host element of the widget's shadow root; `sentry-feedback` is the SDK's default `id`. */
const WIDGET_HOST_ID = 'sentry-feedback'

/*
 * The SDK renders the form inside a `position: fixed; inset: 0` <dialog> whose own click handler
 * closes the form, so the page behind it is unreadable and one stray click discards a half-written
 * report. The panel inside it is already pinned bottom-right (`--inset: auto 0 0 auto`), so letting
 * clicks through leaves exactly that panel and nothing else.
 */
const PASS_THROUGH_STYLES = `
.dialog { background-color: transparent; pointer-events: none; }
.dialog__position { pointer-events: auto; }
`

const makePageUsableBehindTheForm = (): void => {
  /* Opening the form sets `body { overflow: hidden }`, and the app scrolls on the document. */
  document.body.style.overflow = ''

  const shadow = document.getElementById(WIDGET_HOST_ID)?.shadowRoot
  if (!shadow || shadow.querySelector('[data-spcfy-feedback-style]')) return
  const style = document.createElement('style')
  style.setAttribute('data-spcfy-feedback-style', '')
  style.textContent = PASS_THROUGH_STYLES
  shadow.appendChild(style)
}

/*
 * One form, reused. `createForm` appends a fresh dialog to a shadow root it shares with every
 * earlier one, so a form per click piles them up — and each `open()` records the body's overflow to
 * put back on close, so opening a second form over the first records `hidden` and closing then
 * locks the page for good.
 */
let form: FeedbackForm | undefined
let formCrashEventId: string | undefined

/**
 * Opens Sentry's user-feedback form, optionally tied to the crash the user just hit.
 *
 * Deliberately not `showReportDialog`: that renders an iframe served from sentry.io, and this app
 * routes Sentry traffic through `tunnel` precisely because that origin is blocked for some users.
 * The feedback form is in-page and posts over the normal transport, so it follows the tunnel.
 *
 * `feedbackIntegration` is registered with `autoInject: false`, so the form exists only where it is
 * asked for. It is absent when Sentry has no DSN — local dev — which is not a failure.
 */
export const openFeedbackForm = async (crashEventId?: string): Promise<void> => {
  const feedback = Sentry.getFeedback()
  if (!feedback) {
    console.warn('Sentry produced no feedback form; expected when Sentry is not configured')
    return
  }

  if (!form || formCrashEventId !== crashEventId) {
    form?.removeFromDom()
    form = await feedback.createForm(crashEventId ? { tags: { crash_event_id: crashEventId } } : undefined)
    formCrashEventId = crashEventId
  }

  form.appendToDom()
  form.open()
  makePageUsableBehindTheForm()
}

/** Whether a feedback form can be offered at all, so a dead button is never rendered. */
export const feedbackAvailable = (): boolean => Sentry.getFeedback() !== undefined
